import { FacebookLoginProvider, SocialAuthService } from '@abacritt/angularx-social-login';
import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, input, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationMessage } from '@app/core/models';
import { RouteRedirectAfterAction } from '@app/core/route-redirect-after-action';
import { CustomService, DeviceDetectService, RolesService } from '@app/core/services';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { LoggerService } from '@app/core/services/logger/logger.service';
import { ModalManagerService } from '@app/core/services/modal-manager';
import { ModalTypes } from '@app/core/services/modal-manager/modal-types.enum';
import { NotificationsService } from '@app/core/services/notifications/notifications.service';
import { SplashScreenService } from '@app/core/services/splash-screen/splash-screen.service';
import { SubscriptionPlansService } from '@app/core/services/subscription-plans/subscription-plans.service';
import { TrackingService } from '@app/core/services/tracking/tracking.service';
import { LoaderWithWordsService } from '@app/shared';
import { InstructorCoachPromoService } from '@app/shared/modals/instructor-coach-promo/instructor-coach-promo.service';
import { Facebook, FacebookLoginResponse } from '@awesome-cordova-plugins/facebook/ngx';
import { environment } from '@env/environment';
import { LoginCredentials, UserService, ViewService } from '@makiwin/ngx-drupal8-rest';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, take } from 'rxjs';
declare const google: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent extends RouteRedirectAfterAction implements OnInit, OnDestroy {
  loginForm?: FormGroup;
  displayFacebook = true;
  displayApple = this.deviceDetectService.device.platform === 'iOS';
  loading = false;
  /**
   * Input to check if the login is in a modal.
   */
  readonly isInModal = input<boolean>(false);
  /**
   * Event emitter to request the register page.
   */
  @Output() requestRegister: EventEmitter<void> = new EventEmitter<void>(false);
  /**
   * Event emitter to notify the login is done.
   */
  @Output() loginDone: EventEmitter<void> = new EventEmitter<void>();
  /**
   * Event emitter to notify the login is in progress.
   */
  @Output() isLogging: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  notificationMessage: NotificationMessage = {
    type: 'danger',
    message: '',
    autoHide: true,
    close: true,
    icon: 'icon icon-circle-exclamation',
  };

  protected readonly displayGoogle = signal(true);

  private userSubscribe?: Subscription;
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly authSessionService: AuthSessionService = inject(AuthSessionService);
  private readonly trackingService: TrackingService = inject(TrackingService);
  private readonly loggerService: LoggerService = inject(LoggerService);

  constructor(
    public deviceDetectService: DeviceDetectService,
    private formBuilder: FormBuilder,
    private facebook: Facebook,
    private socialAuthService: SocialAuthService,
    private translateService: TranslateService,
    private userService: UserService,
    private rolesService: RolesService,
    private customService: CustomService,
    private modalManagerService: ModalManagerService,
    private loaderWords: LoaderWithWordsService,
    private coachPromoService: InstructorCoachPromoService,
    private subscriptionPlansService: SubscriptionPlansService,
    private splashScreenService: SplashScreenService,
    private viewService: ViewService
  ) {
    super();
  }

  ngOnInit() {
    if (this.deviceDetectService.isCordova) {
      this.navDataService.displayHeader = false;
    }
    this.userSubscribe = this.authSessionService.currentUserProfile$.pipe(take(1)).subscribe((user) => {
      if (user) {
        this.navDataService.navigate('/account');
      }
    });
    this.displayApple = this.deviceDetectService.device.platform === 'iOS';
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.navDataService.displayHeader = true;
    this.userSubscribe?.unsubscribe();
  }

  /**
   * Handle the login form submit.
   */
  protected login() {
    // if the form is not valid, do nothing

    if (!this.loginForm?.valid || this.loading) {
      this.loginForm?.markAllAsTouched();
      this.loginForm?.updateValueAndValidity();
      return;
    }
    // getting the values from the login form

    const loginCredentials: LoginCredentials = this.loginForm?.value;
    // use user service to login
    this.loaderWords.show();
    // this.loading = true;
    this.userService.login(loginCredentials).subscribe({
      next: () => {
        this.navigateToHome();
      },
      error: (err: any) => {
        this.loaderWords.hide();
        this.notificationsService.notifyError(
          this.translateService.instant(err?.error?.message ?? err.message ?? 'Error login in the app')
        );
      },
    });
    return false;
  }

  /**
   * Handle the login with apple ID.
   */
  protected loginApple() {
    this.loaderWords.show();
    this.customService.appleLogin().subscribe({
      next: () => {
        this.navigateToHome();
      },
      error: (err) => {
        const genericMessage = this.translateService.instant('An error occured while login with Apple');
        this.notificationsService.notifyError(
          err?.status === 0 ? genericMessage : err?.error?.message ?? err.message ?? genericMessage
        );
        this.loaderWords.hide();
      },
    });
  }

  /**
   * handle the login with facebook function
   */
  loginFacebook() {
    this.loaderWords.show();
    if (!this.deviceDetectService.isCordova) {
      this.socialAuthService
        .signIn(FacebookLoginProvider.PROVIDER_ID)
        .then((response) => {
          this.loginWithFacebookToken(response.authToken);
        })
        .catch((err) => this.handleFacebookLoginError(err));
      return;
    }
    this.facebook.login(['public_profile', 'email']).then(
      (res: FacebookLoginResponse) => {
        if (res.status !== 'connected') {
          this.notificationsService.notifyError(this.translateService.instant('Error on login with facebook'));
          this.loaderWords.hide();
          return;
        }
        this.loginWithFacebookToken(res.authResponse.accessToken);
      },
      (err) => {
        this.handleFacebookLoginError(err);
      }
    );
  }

  /**
   * Check if membership is expired and open the modal.
   */
  protected checkMembership(previousRole?: string) {
    if (this.rolesService.isExpired && this.rolesService.checkIfRoleIsProInstructor(previousRole)) {
      this.modalManagerService.open(ModalTypes.renewMembership).subscribe();
    } else {
      this.coachPromoService.open();
    }
  }

  /**
   * Open the password reset page.
   */
  protected openPassReset() {
    if (this.isInModal()) {
      this.loginDone.emit();
    }
    this.navDataService.navigate('/user/password', environment.needD7);
  }

  /**
   * Try to login with the facebook token.
   *
   * @param authToken
   *  Authentication token from facebook.
   */
  protected loginWithFacebookToken(authToken: string) {
    this.splashScreenService.show();
    this.customService.facebookConnect(authToken).subscribe({
      next: () => {
        this.navigateToHome();
      },
      error: (err) => {
        this.loaderWords.hide();
        this.splashScreenService.hide();
        this.notificationsService.notifyError(err?.error?.message ?? err.message ?? 'Error login in the app');
      },
    });
  }

  /**
   * Handles the Google login button click event.
   *
   * This method initializes the Google OAuth2 token client and requests an access token
   * with the specified scopes. Upon receiving a valid access token, it triggers the
   * login process via the custom service and navigates to the home page on success.
   * If an error occurs during the login process or token retrieval, it displays an
   * appropriate error notification.
   *
   * @param $event - The click event triggered by the user.
   */
  protected handleClickGoogleLogin($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    google.accounts.oauth2
      .initTokenClient({
        client_id: environment.googleClientId,
        scope: 'https://www.googleapis.com/auth/userinfo.profile', // or any scopes you need
        callback: (response: any) => {
          if (response.access_token) {
            this.loaderWords.show();
            this.customService.googleLogin(response.access_token).subscribe({
              next: () => this.navigateToHome('/profile'),
              error: (err) => {
                this.loaderWords.hide();
                this.splashScreenService.hide();
                this.notificationsService.notifyError(err?.error?.message ?? err.message ?? 'Error login in the app');
              },
            });
            // You can now call Google APIs with this token
          } else {
            this.notificationsService.notifyError('Error on login with Google');
            console.error('Token response error:', response);
          }
        },
      })
      .requestAccessToken();
  }

  /**
   * Handle the facebook login error.
   *
   * @param err
   *  Error from facebook login.
   */
  private handleFacebookLoginError(err: any) {
    // 4201 it's the error code for the user cancel the login.
    if (err?.errorCode !== '4201') {
      this.notificationsService.notifyError(this.translateService.instant('Error on login with facebook'));
    }
    this.loaderWords.hide();
    this.loggerService.logError('FB Login: ' + err.message);
  }

  /**
   * Build the login form using form builder.
   */
  private buildForm() {
    this.loginForm = this.formBuilder.group({
      name: ['', Validators.required],
      pass: ['', Validators.required],
    });
    // this.loginForm.valueChanges.subscribe((values) => {
    //   this.displayApple = this.deviceDetectService.device.platform === 'iOS';
    //   //this.displayApple = !values.name && this.deviceDetectService.device.platform === 'iOS';
    //   // this.displayFacebook = !values.name;
    //   // this.displayGoogle.set(!values.name);
    // });
  }

  /**
   * Navigates the user to the home page or a specified path after initializing
   * necessary services and tracking the login event. This method handles user
   * session initialization, role setup, push notification setup, and membership
   * checks. It also manages UI elements like splash screens and loaders.
   *
   * @param path - The path to navigate to after login. Defaults to '/account'.
   *
   */
  private navigateToHome(path: string = '/account'): void {
    this.splashScreenService.show();
    this.authSessionService.initProfile().subscribe((resUser: any) => {
      // this.customService.trackLocation();
      this.rolesService.init(resUser);
      // init push notification
      this.customService.initPush();
      this.navDataService.resetHeaderRightButton();
      this.subscriptionPlansService.get().subscribe();

      this.trackingService.trackEvent({
        event: 'login',
        dl_name: 'login_title',
        dl_value: `Login ${resUser.name}`,
        userId: resUser.uid,
        userGroups: JSON.stringify(resUser.current_role),
        customer: JSON.stringify({ email: resUser.mail }),
      });

      this.loading = false;
      // update the user timezone
      // this.customService.updateUserTimeZone();
      if (this.isInModal()) {
        this.loginDone.emit();
      } else {
        this.executeNavigation(path);
        this.checkMembership(resUser.previous_role?.id);
      }
      this.loaderWords.hide();
      this.splashScreenService.hide();
    });
    // Display coach promo popup for instructors
    // Change to cta btns if same feature required for different roles and handle inside the modal component :)
  }
}
